import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { imageSrc, resolveChannelLogoImage } from "@shared/vod-images";
import { tokens } from "../theme";
import {
  foyerApi,
  type ChannelRow,
  type ContentPackageRow,
  type PackageOverlapReport,
} from "../http/foyer";
import { friendlyError } from "../utils/apiError";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import { EditDrawer, type Dismiss } from "./EditDrawer";

/**
 * Pick the live channels a package pins.
 *
 * ## Why a picker exists at all, on a screen built to retire enumeration
 *
 * `BE-30` replaced enumerated membership with rules, and this screen was built
 * around that: set a supplier rule, preview the diff, save. Pins survived as a
 * COUNT in a table cell and nowhere else — there has never been a way to add
 * one.
 *
 * That is right for VOD and wrong for channels, and the difference is not a
 * matter of taste:
 *
 * > **A rule says "everything from this supplier". No rule says "SVT1, SVT2,
 * > TV4 and Kanal 5".** A channel line-up is a commercial selection, not a
 * > property of the content, so they can only ever be pinned.
 *
 * With no way to author them, `basic` could not be built in Foyer at all — the
 * package that every hotel starts from. The rule editor could describe the
 * catalogue and never the offer.
 *
 * ## The catalogue is not ours and is not copied
 *
 * The channel list comes live from `nenda-content-api` on every open. Names,
 * logos and stream URLs live there, frozen and shared with the portal, and
 * Foyer stores **only the `assetId` strings** a package pins.
 *
 * That is the whole answer to "how do the channels get in": they are already
 * in. What the portal held that Foyer does not want is its OPINION about which
 * channels belong together — nine copies of `basic-sweden` over a union of ten
 * channels, and a `kids-sweden` that is 740 titles in some business units and
 * zero in others. The opinion is what we are re-authoring; the channels were
 * never the portal's to begin with.
 *
 * ## Pins are shown, not merged
 *
 * A package may carry BOTH pinned ids and a rule. This dialog edits the pinned
 * says so, because a picker that quietly dropped a rule — or that showed
 * rule-matched titles as if they were ticked — would be a second, weaker answer
 * to a question the rule editor already answers properly.
 *
 * ⚠️ A pinned id that is no longer in the catalogue is shown rather than
 * silently dropped. `BE-18` found real drift, the portal's `DELETE
 * /cms/assets/:id` prunes nothing, and an id that resolves to nothing is
 * exactly what an operator needs to see. Unticking is how it gets removed;
 * saving never removes one behind their back.
 */

const PAGE_SIZE = 200;

export default function PackageChannelPicker({
  pkg,
  onClose,
  onSaved,
}: {
  pkg: ContentPackageRow;
  onClose: () => void;
  onSaved: (overlap: PackageOverlapReport | null) => Promise<void>;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [channels, setChannels] = useState<ChannelRow[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set(pkg.assets));
  const [filter, setFilter] = useState("");
  /**
   * Which slice of the catalogue to show.
   *
   * The grid used to be every channel in catalogue order with the pinned ones
   * merely highlighted, so the question this dialog exists to answer — WHAT IS
   * IN THIS PACKAGE — could only be answered by scanning several hundred cards
   * for a border colour. Reported by Sigge: *"it lists all channels and
   * highlight those that has previously been selected and creates a hard time
   * to see"*.
   *
   * A scope filter rather than sorting the picked ones to the top: with
   * sorting, a card jumps out from under the cursor the moment you tick it,
   * which makes picking several in a row a game of chase. Positions stay put
   * here, and "In package" answers the question outright.
   */
  const [scope, setScope] = useState<"all" | "in" | "out">("all");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await foyerApi.listDistributionChannels({
        pageSize: PAGE_SIZE,
      });
      setChannels(page.channels);
    } catch (e) {
      setError(friendlyError(t, e));
      setChannels([]);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Pinned ids the catalogue did not return.
   *
   * Kept visible and kept TICKED: they are still in the package, and the whole
   * point of surfacing them is that an operator decides, rather than a save
   * quietly tidying them away.
   */
  const orphans = useMemo(() => {
    if (!channels) return [];
    const live = new Set(channels.map((c) => c.assetId));
    return pkg.assets.filter((id) => !live.has(id));
  }, [channels, pkg.assets]);

  const visible = useMemo(() => {
    if (!channels) return [];
    const q = filter.trim().toLowerCase();
    return channels.filter((c) => {
      if (scope === "in" && !picked.has(c.assetId)) return false;
      if (scope === "out" && picked.has(c.assetId)) return false;
      if (!q) return true;
      return (
        (c.originalTitle ?? "").toLowerCase().includes(q) ||
        c.assetId.toLowerCase().includes(q)
      );
    });
  }, [channels, filter, scope, picked]);

  const toggle = (assetId: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const dirty = useMemo(() => {
    const before = new Set(pkg.assets);
    if (before.size !== picked.size) return true;
    for (const id of picked) if (!before.has(id)) return true;
    return false;
  }, [picked, pkg.assets]);

  const save = async (dismiss: Dismiss) => {
    setBusy(true);
    setError(null);
    try {
      // `assets` REPLACES. The route's own semantics, and correct here because
      // this dialog holds the complete set: it started from `pkg.assets` and
      // every change since has been a tick.
      const res = await foyerApi.updateContentPackage(pkg.id, {
        assets: [...picked],
      });
      // `ENT-3`: the pin path is the one that produced nine copies of
      // `basic-sweden`, so the near-copy warning has to reach the page from
      // here too — not only from the rule dialog.
      // Slide away first; the page then reloads and unmounts the drawer.
      toast.success(
        t("packageChannelPicker.saved", { name: pkg.name, count: picked.size })
      );
      dismiss(() => void onSaved(res.overlap));
    } catch (e) {
      setError(friendlyError(t, e));
      toast.error(
        describeError(t, "savePackageChannels", e, { package: pkg.name })
      );
      setBusy(false);
    }
  };

  return (
    <EditDrawer
      open
      onClose={onClose}
      busy={busy}
      title={t("packageChannelPicker.title", { name: pkg.name || pkg.id })}
      subtitle={
        <>
          {t("packageChannelPicker.pinsOnly")}{" "}
          {pkg.rule
            ? t("packageChannelPicker.alsoRule")
            : t("packageChannelPicker.liveChannels")}
        </>
      }
      width={900}
      data-testid="package-channels-drawer"
      actions={(dismiss) => (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mr: "auto", alignSelf: "center" }}
          >
            {t("packageChannelPicker.pinned", { count: picked.size })}
          </Typography>
          <Button onClick={() => dismiss()} disabled={busy}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void save(dismiss)}
            disabled={busy || !dirty}
            data-testid="pkg-save-assets"
          >
            {t("common:buttons.save")}
          </Button>
        </>
      )}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="info">{t("packageChannelPicker.intro")}</Alert>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            label={t("common:labels.filter")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t("packageChannelPicker.filterPlaceholder")}
            sx={{ flex: 1 }}
          />
          {/* The counts are on the buttons on purpose: "In package (0)"
                answers the question without a click, and an operator who has
                just emptied a package can see that they did. */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={scope}
            onChange={(_, v) => v && setScope(v as typeof scope)}
            aria-label={t("packageChannelPicker.scopeLabel")}
          >
            <ToggleButton value="all" data-testid="scope-all">
              {t("packageChannelPicker.scopeAll", {
                count: channels?.length ?? 0,
              })}
            </ToggleButton>
            <ToggleButton value="in" data-testid="scope-in">
              {t("packageChannelPicker.scopeIn", { count: picked.size })}
            </ToggleButton>
            <ToggleButton value="out" data-testid="scope-out">
              {t("packageChannelPicker.scopeOut")}
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {orphans.length > 0 ? (
          <Alert severity="warning" data-testid="pkg-orphan-assets">
            {t("packageChannelPicker.orphans", { count: orphans.length })}
            <Box sx={{ mt: 1 }}>
              {orphans.map((id) => (
                <Chip
                  key={id}
                  size="small"
                  label={id}
                  onDelete={() => toggle(id)}
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          </Alert>
        ) : null}

        {channels === null ? (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : visible.length === 0 ? (
          <Alert severity="info">
            {scope === "in" && !filter.trim()
              ? t("packageChannelPicker.nothingPinned")
              : filter.trim()
                ? t("packageChannelPicker.noMatch")
                : t("packageChannelPicker.noChannels")}
          </Alert>
        ) : (
          <Box
            sx={{
              // No inner scroll box: the drawer body scrolls. A capped height
              // here squeezed every card to fit it - an `overflow: hidden`
              // grid item may shrink below its content, so rows collapsed and
              // clipped the logo plate and the name row.
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
              gridAutoRows: "max-content",
              gap: 1.5,
              p: 0.5,
            }}
          >
            {visible.map((c) => (
              <ChannelCard
                key={c.assetId}
                channel={c}
                picked={picked.has(c.assetId)}
                onToggle={() => toggle(c.assetId)}
              />
            ))}
          </Box>
        )}
      </Stack>
    </EditDrawer>
  );
}

/**
 * One channel, as a card you click.
 *
 * ## Why a card and not a row
 *
 * The first version was a checkbox list with the `assetId` under each name, and
 * Sigge's note on meeting it was exact: the id "doesn't really do anything in
 * this view". True — an operator building a line-up recognises SVT1 by its
 * mark, not by `SVT1_20536C`, and a column of near-identical ids is noise
 * standing between them and the thing they came to do.
 *
 * The id is still on the card as a `title`, so it is one hover away when
 * somebody is matching a log line to a screen at eleven at night. Removed from
 * the face, not from the page.
 *
 * ## The whole card is the control
 *
 * Not a checkbox with a label beside it: a 150px target rather than a 20px one,
 * on a screen where an operator ticks a dozen channels in a row. The checkbox
 * stays visible because it is what makes "this is a multi-select" legible at a
 * glance — but it is decoration over a click target that is the entire tile.
 *
 * ## The mark falls back rather than failing
 *
 * A channel with no logo gets a generated tile from its own id, the same device
 * `ChannelsPage` uses. Deterministic, so a channel looks the same everywhere,
 * and it means a catalogue gap reads as a design rather than as a broken image.
 */
function ChannelCard({
  channel,
  picked,
  onToggle,
}: {
  channel: ChannelRow;
  picked: boolean;
  onToggle: () => void;
}) {
  const image = resolveChannelLogoImage({
    images: channel.images ?? [],
    language: "sv",
    languages: ["sv", "en"],
  });
  const src = image ? imageSrc(image, { width: 240, height: 160 }) : null;
  const title = channel.originalTitle || channel.assetId;

  const hue =
    ([...channel.assetId].reduce((n, c) => n + c.charCodeAt(0), 0) * 7) % 360;
  const initials = title
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");

  return (
    <Box
      role="checkbox"
      aria-checked={picked}
      aria-label={title}
      tabIndex={0}
      title={channel.assetId}
      data-testid={`pick-${channel.assetId}`}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onToggle();
        }
      }}
      sx={{
        position: "relative",
        cursor: "pointer",
        borderRadius: 2,
        overflow: "hidden",
        border: 2,
        borderColor: picked ? "primary.main" : tokens.divider,
        transition: "border-color 120ms, box-shadow 120ms",
        "&:hover": { borderColor: picked ? "primary.main" : "text.disabled" },
        "&:focus-visible": {
          outline: 2,
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          height: 76,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 1.25,
          /* A channel logo is usually a dark mark on transparency, so it needs
             a LIGHT plate to read — and the same plate in both themes, or the
             mark disappears into a dark surface. This is the one place a fixed
             colour beats a token: the artwork is not theme-aware, so the thing
             behind it must not be either. */
          background: src ? "#fff" : undefined,
          backgroundImage: src
            ? undefined
            : `linear-gradient(135deg, hsl(${hue} 45% 46%), hsl(${
                (hue + 35) % 360
              } 42% 34%))`,
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt=""
            sx={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        ) : (
          <Typography
            sx={{
              color: "#fff",
              fontWeight: "fontWeightBold",
              typography: "headline",
            }}
          >
            {initials}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          px: 0.75,
          py: 0.5,
          // Its own surface, and a border between: against the white plate
          // above, an unstyled row reads as part of the artwork, which is how
          // the checkbox managed to be invisible in a grid of white cards.
          bgcolor: picked ? "action.selected" : "background.paper",
          borderTop: 1,
          borderColor: tokens.divider,
        }}
      >
        <Checkbox
          size="small"
          checked={picked}
          tabIndex={-1}
          // The card owns the click; the box would otherwise toggle twice.
          onClick={(e) => e.stopPropagation()}
          onChange={onToggle}
          checkedIcon={<CheckCircleIcon fontSize="small" />}
          sx={{ p: 0.25 }}
        />
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontWeight: picked ? "fontWeightSemibold" : "fontWeightRegular",
          }}
        >
          {title}
        </Typography>
      </Stack>
    </Box>
  );
}
